import { clamp, len } from "./math";

// Unified input: keyboard (WASD/arrows), gamepad left stick, plus a few
// action buttons. Exposes a normalized move vector and edge-triggered actions.
export class Input {
  private keys = new Set<string>();
  private pressedThisFrame = new Set<string>();
  private prevButtons = new Map<number, boolean>();

  moveX = 0;
  moveY = 0;

  /** Edge-triggered actions consumed once per press. */
  confirm = false;
  /** Menu confirm that EXCLUDES Space (Space is the fire key) — for upgrade picks. */
  menuConfirm = false;
  ultimate = false;
  pause = false;

  // Selection nudges for menus (edge-triggered).
  navX = 0;
  navY = 0;

  /** Number key pressed this frame (1..9), or 0 if none. */
  digit = 0;

  /** Mute toggle (M), edge-triggered. */
  mute = false;

  /** Title-screen accessibility toggles (K = screen shake, F = reduce flashing). */
  toggleShake = false;
  toggleFlash = false;

  /** Manual fire (Space / gamepad RT), edge-triggered — one shot per press. */
  shoot = false;

  // ── Touch ──────────────────────────────────────────────────────────────────
  /** Taps that started this frame (CSS px). Menus hit-test these; the right half fires. */
  frameTaps: { x: number; y: number }[] = [];
  /** Move joystick (left half of the screen) state, for rendering. */
  joyActive = false;
  joyBaseX = 0;
  joyBaseY = 0;
  joyDX = 0;
  joyDY = 0;

  private pendingTaps: { x: number; y: number }[] = [];
  private joyId: number | null = null;
  private uiUltimate = false; // set by the on-screen SURGE button
  private uiPause = false; // set by the on-screen pause button

  /** Radius (CSS px) of joystick travel for a full-tilt move vector. */
  private static readonly JOY_RANGE = 60;

  /** Canvas used to map touch clientX/Y into canvas-local coordinates. */
  private canvas: HTMLCanvasElement | null;

  constructor(target: Window = window, canvas: HTMLCanvasElement | null = null) {
    this.canvas = canvas;
    target.addEventListener("keydown", (e) => {
      if (e.repeat) return;
      const k = e.key.toLowerCase();
      this.keys.add(k);
      this.pressedThisFrame.add(k);
      if (["arrowup", "arrowdown", "arrowleft", "arrowright", " "].includes(k)) {
        e.preventDefault();
      }
    });
    target.addEventListener("keyup", (e) => this.keys.delete(e.key.toLowerCase()));
    // Desktop: a mouse click counts as a tap so menus (title/lab/level-up) are
    // clickable. (On-screen touch buttons stopPropagation, so no double-fire.)
    target.addEventListener("mousedown", (e) => {
      const rect = this.canvas?.getBoundingClientRect();
      this.pendingTaps.push({ x: e.clientX - (rect?.left ?? 0), y: e.clientY - (rect?.top ?? 0) });
    });
    target.addEventListener("blur", () => {
      this.keys.clear();
      this.joyId = null;
      this.joyActive = false;
      this.joyDX = this.joyDY = 0;
    });

    this.initTouch(target);
    this.initButtons();
  }

  /** Map a touch's viewport coords into canvas-local CSS pixels. */
  private localPoint(t: Touch): { x: number; y: number } {
    const rect = this.canvas?.getBoundingClientRect();
    return { x: t.clientX - (rect?.left ?? 0), y: t.clientY - (rect?.top ?? 0) };
  }

  private canvasWidth(): number {
    return this.canvas?.getBoundingClientRect().width ?? window.innerWidth;
  }

  private initTouch(target: Window): void {
    const onStart = (e: TouchEvent) => {
      const halfW = this.canvasWidth() / 2;
      for (const t of Array.from(e.changedTouches)) {
        const { x, y } = this.localPoint(t);
        // First touch on the left half drives the (dynamic) move joystick.
        if (this.joyId === null && x < halfW) {
          this.joyId = t.identifier;
          this.joyBaseX = x;
          this.joyBaseY = y;
          this.joyDX = 0;
          this.joyDY = 0;
          this.joyActive = true;
        }
        // Every touch is also recorded as a tap (menus / right-half fire).
        this.pendingTaps.push({ x, y });
      }
      e.preventDefault();
    };
    const onMove = (e: TouchEvent) => {
      for (const t of Array.from(e.changedTouches)) {
        if (t.identifier === this.joyId) {
          const { x, y } = this.localPoint(t);
          this.joyDX = x - this.joyBaseX;
          this.joyDY = y - this.joyBaseY;
        }
      }
      e.preventDefault();
    };
    const onEnd = (e: TouchEvent) => {
      for (const t of Array.from(e.changedTouches)) {
        if (t.identifier === this.joyId) {
          this.joyId = null;
          this.joyActive = false;
          this.joyDX = this.joyDY = 0;
        }
      }
    };
    target.addEventListener("touchstart", onStart, { passive: false });
    target.addEventListener("touchmove", onMove, { passive: false });
    target.addEventListener("touchend", onEnd);
    target.addEventListener("touchcancel", onEnd);
  }

  private initButtons(): void {
    const bind = (id: string, fn: () => void): void => {
      const el = document.getElementById(id);
      if (!el) return;
      const press = (e: Event) => {
        e.preventDefault();
        e.stopPropagation(); // don't let it count as a canvas tap / joystick grab
        fn();
      };
      el.addEventListener("touchstart", press, { passive: false });
      el.addEventListener("mousedown", press);
    };
    bind("btn-ultimate", () => (this.uiUltimate = true));
    bind("btn-pause", () => (this.uiPause = true));
  }

  private down(...names: string[]): boolean {
    return names.some((n) => this.keys.has(n));
  }

  private hit(...names: string[]): boolean {
    return names.some((n) => this.pressedThisFrame.has(n));
  }

  /** Call once per frame, before reading input state. */
  poll(): void {
    let dx = 0;
    let dy = 0;
    if (this.down("a", "arrowleft")) dx -= 1;
    if (this.down("d", "arrowright")) dx += 1;
    if (this.down("w", "arrowup")) dy -= 1;
    if (this.down("s", "arrowdown")) dy += 1;

    this.navX = (this.hit("d", "arrowright") ? 1 : 0) - (this.hit("a", "arrowleft") ? 1 : 0);
    this.navY = (this.hit("s", "arrowdown") ? 1 : 0) - (this.hit("w", "arrowup") ? 1 : 0);

    this.confirm = this.hit("enter", " ", "e");
    this.menuConfirm = this.hit("enter", "e"); // no Space — avoids fire-spam selecting
    this.ultimate = this.hit("r", "shift");
    this.pause = this.hit("escape", "p");
    this.mute = this.hit("m");
    this.toggleShake = this.hit("k");
    this.toggleFlash = this.hit("f");
    this.shoot = this.hit(" "); // edge-triggered: tap to fire, holding won't repeat

    this.digit = 0;
    for (let d = 1; d <= 9; d++) {
      if (this.pressedThisFrame.has(String(d))) {
        this.digit = d;
        break;
      }
    }

    // Gamepad (first connected pad) overrides keyboard if its stick is active.
    const pads = navigator.getGamepads?.() ?? [];
    const pad = pads.find((p) => p);
    if (pad) {
      const lx = deadzone(pad.axes[0] ?? 0);
      const ly = deadzone(pad.axes[1] ?? 0);
      if (lx !== 0 || ly !== 0) {
        dx = lx;
        dy = ly;
      }
      if (this.padHit(pad, 0)) {
        this.confirm = true; // A
        this.menuConfirm = true;
      }
      if (this.padHit(pad, 1)) this.pause = true; // B
      if (this.padHit(pad, 2)) this.ultimate = true; // X
      if (this.padHit(pad, 7)) this.shoot = true; // RT — edge-triggered
    }

    // Touch move joystick overrides keyboard when engaged.
    if (this.joyActive) {
      let jx = this.joyDX / Input.JOY_RANGE;
      let jy = this.joyDY / Input.JOY_RANGE;
      const jm = len(jx, jy);
      if (jm > 1) {
        jx /= jm;
        jy /= jm;
      }
      if (Math.abs(jx) > 0.06 || Math.abs(jy) > 0.06) {
        dx = jx;
        dy = jy;
      }
    }

    // On-screen buttons fold into the edge-triggered actions.
    if (this.uiUltimate) this.ultimate = true;
    if (this.uiPause) this.pause = true;
    this.uiUltimate = false;
    this.uiPause = false;

    // Publish this frame's taps; a tap on the right half also fires.
    this.frameTaps = this.pendingTaps;
    this.pendingTaps = [];
    const halfW = this.canvasWidth() / 2;
    if (this.frameTaps.some((t) => t.x > halfW)) this.shoot = true;

    const m = len(dx, dy);
    if (m > 1) {
      dx /= m;
      dy /= m;
    }
    this.moveX = clamp(dx, -1, 1);
    this.moveY = clamp(dy, -1, 1);

    this.pressedThisFrame.clear();
  }

  private padHit(pad: Gamepad, index: number): boolean {
    const now = pad.buttons[index]?.pressed ?? false;
    const was = this.prevButtons.get(index) ?? false;
    this.prevButtons.set(index, now);
    return now && !was;
  }
}

function deadzone(v: number, dz = 0.22): number {
  if (Math.abs(v) < dz) return 0;
  return (v - Math.sign(v) * dz) / (1 - dz);
}
